import * as XLSX from 'xlsx'
import { renderToStaticMarkup } from 'react-dom/server'
import PhoneVisibilityCell from '../components/common/PhoneVisibilityCell'

// Tiêu đề các cột thao tác — tự động loại khỏi file export.
const SKIP_TITLES = ['Hành động', 'Thao tác', 'Hành Động', 'Thao Tác']

/**
 * Xuất Excel (.xlsx) phía FE dựa trên đúng các cột đang hiển thị trên bảng.
 *
 * Nguyên tắc: "table hiển thị thế nào thì excel thế" — tái sử dụng chính mảng
 * `columns` của antd Table. Với mỗi ô, ưu tiên theo thứ tự:
 *   1. col.exportValue(record, index)  -> chủ động override (vd: cột SĐT trả số thật)
 *   2. col.render(value, record, index) -> chuyển JSX thành text
 *   3. record[dataIndex]
 *
 * Cột có `col.exportSkip === true` (vd: cột "Thao tác") sẽ bị loại khỏi file.
 */

const getByPath = (obj, path) => {
  if (obj == null || path == null) return undefined
  if (Array.isArray(path)) {
    return path.reduce((acc, key) => (acc == null ? acc : acc[key]), obj)
  }
  if (typeof path === 'string' && path.includes('.')) {
    return path.split('.').reduce((acc, key) => (acc == null ? acc : acc[key]), obj)
  }
  return obj[path]
}

const decodeEntities = (str) =>
  str
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")

// Tìm đệ quy phần tử PhoneVisibilityCell trong cây node và trả về SĐT thật
// (prop `value`) — để export luôn ra số đầy đủ, kể cả khi bảng đang che số.
const findPhoneValue = (node) => {
  if (node == null || typeof node !== 'object') return undefined
  if (Array.isArray(node)) {
    for (const child of node) {
      const v = findPhoneValue(child)
      if (v !== undefined) return v
    }
    return undefined
  }
  if (node.type === PhoneVisibilityCell) {
    return node.props?.value
  }
  const children = node.props?.children
  if (children !== undefined) return findPhoneValue(children)
  return undefined
}

// Chuyển 1 node (primitive hoặc React element) thành chuỗi text hiển thị.
const nodeToText = (node) => {
  if (node == null || node === false || node === true) return ''
  if (typeof node === 'string') return node
  if (typeof node === 'number') return String(node)
  if (Array.isArray(node)) return node.map(nodeToText).join('')
  try {
    const html = renderToStaticMarkup(node)
    return decodeEntities(html.replace(/<[^>]+>/g, ' '))
      .replace(/\s+/g, ' ')
      .trim()
  } catch (e) {
    return ''
  }
}

const getHeader = (col, idx) => {
  if (typeof col.title === 'string') return col.title
  if (typeof col.title === 'number') return String(col.title)
  return col.key != null ? String(col.key) : `Cột ${idx + 1}`
}

const getCellText = (col, record, index) => {
  try {
    if (typeof col.exportValue === 'function') {
      const v = col.exportValue(record, index)
      return v == null ? '' : nodeToText(v)
    }
    const raw = getByPath(record, col.dataIndex)
    if (typeof col.render === 'function') {
      const node = col.render(raw, record, index)
      const phone = findPhoneValue(node)
      if (phone !== undefined) return phone == null ? '' : String(phone)
      return nodeToText(node)
    }
    return raw == null ? '' : nodeToText(raw)
  } catch (e) {
    const raw = getByPath(record, col.dataIndex)
    return raw == null ? '' : String(raw)
  }
}

/**
 * @param {Object}   params
 * @param {Array}    params.columns   Mảng columns của antd Table (initialColumns)
 * @param {Array}    params.data      Danh sách bản ghi đang hiển thị (data.list)
 * @param {string}   params.fileName  Tên file (không cần đuôi .xlsx)
 * @param {string}  [params.sheetName='Sheet1']
 */
export const exportTableToExcel = ({
  columns = [],
  data = [],
  fileName = 'export',
  sheetName = 'Sheet1'
}) => {
  const exportColumns = columns.filter(
    (col) =>
      col &&
      col.exportSkip !== true &&
      !(typeof col.title === 'string' && SKIP_TITLES.includes(col.title.trim())) &&
      (col.title != null || col.dataIndex != null)
  )

  const header = exportColumns.map((col, idx) => getHeader(col, idx))
  const rows = (data || []).map((record, index) =>
    exportColumns.map((col) => getCellText(col, record, index))
  )

  const aoa = [header, ...rows]
  const worksheet = XLSX.utils.aoa_to_sheet(aoa)

  // Ước lượng độ rộng cột theo nội dung
  worksheet['!cols'] = header.map((h, colIdx) => {
    const maxLen = aoa.reduce((max, row) => {
      const cell = row[colIdx]
      const len = cell == null ? 0 : String(cell).length
      return len > max ? len : max
    }, 10)
    return { wch: Math.min(Math.max(maxLen + 2, 10), 60) }
  })

  const workbook = XLSX.utils.book_new()
  XLSX.utils.book_append_sheet(workbook, worksheet, sheetName)

  const stamp = new Date()
    .toLocaleDateString('vi-VN')
    .replace(/\//g, '-')
  XLSX.writeFile(workbook, `${fileName}_${stamp}.xlsx`)
}

export default exportTableToExcel
